import React, { FC, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import VoyageNomadeSearchForm from "./(voyage-nomade-search-form)/VoyageNomadeSearchForm";
import VoyageSedentaireSearchForm from "./(voyage-sedentaire-search-form)/VoyageSedentaireSearchForm";
import VoyageIASearchForm from "./(voyage-ia-search-form)/VoyageIASearchForm";
import Lottie from 'lottie-react';
import animationData from '../../shared/loading.json';
import { useTranslation } from 'react-i18next';

// Définir les clés de traduction
const TRANSLATION_KEYS = {
  voyageNomade: 'voyageNomade',
  voyageSedentaire: 'voyageSedentaire',
  voyageIA: 'assistantIA',
};

export type SearchTab = "voyageNomade" | "voyageSedentaire"  | "voyageIA";

export interface HeroSearchFormProps {
  className?: string;
  currentTab?: SearchTab;
}

const HeroSearchForm: FC<HeroSearchFormProps> = ({
  className = "",
  currentTab = "voyageNomade",
}) => {
  const { t } = useTranslation();

  const tabs: SearchTab[] = ["voyageNomade", "voyageSedentaire", "voyageIA"];
  const [tabActive, setTabActive] = useState<SearchTab>(currentTab);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChangeLoading = () => {
    setIsLoading(prevState => !prevState);
  }


const getTabIcon = (tab: SearchTab) => {
  switch (tab) {
    case "voyageNomade":
      return "la-suitcase-rolling";
    case "voyageSedentaire":
      return "la-city";
    case "voyageIA":
      return "";
    default:
      return "la-circle";
  }
};

const renderTab = () => {
  return (
    <div
      className="
        mb-0
        flex
        overflow-x-auto
        hiddenScrollbar

        gap-3
        px-1
        sm:px-0
      "
    >
      {tabs.map((tab) => {
        const active = tab === tabActive;

        return (
          <button
            type="button"
            key={tab}
            onClick={() => setTabActive(tab)}
            className={`
              flex-shrink-0

              inline-flex
              items-center
              gap-2

              rounded-full

              px-5
              py-3

              text-sm
              lg:text-base
              font-semibold

              border

              shadow-sm

              transition-all
              duration-200

              ${
                active
                  ? `
                    bg-rose-50
                    text-rose-500
                    border-rose-100
                    shadow-rose-100/80
                  `
                  : `
                    bg-white
                    text-neutral-500
                    border-neutral-200
                    hover:text-violet-600
                    hover:border-violet-100
                    hover:bg-violet-50
                    dark:bg-neutral-900
                    dark:border-neutral-700
                    dark:text-neutral-300
                  `
              }
            `}
          >
            <i
              className={`
                las
                ${getTabIcon(tab)}
                text-lg

                ${
                  active
                    ? "text-rose-500"
                    : "text-violet-500"
                }
              `}
            />

            <span>{t(TRANSLATION_KEYS[tab])}</span>
          </button>
        );
      })}
    </div>
  );
};

  const renderForm = () => {
    switch (tabActive) {
      case "voyageNomade":
        return <VoyageNomadeSearchForm onChangeLoading={handleChangeLoading}/>;
      case "voyageSedentaire":
        return <VoyageSedentaireSearchForm onChangeLoading={handleChangeLoading}/>;
      case "voyageIA":
        return <VoyageIASearchForm onChangeLoading={handleChangeLoading}/>;
      default:
        return null;
    }
  };

  return (
    <>
      <div  className={`nc-HeroSearchForm w-full max-w-6xl py-5 lg:py-0 ${className}`}>
        {renderTab()}
        {renderForm()}
      </div>

      {isLoading && (
        <Transition appear show={true} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-[100] overflow-y-auto"
            onClose={() => {}} // Pas de fermeture pour la modale d'attente
          >
            <div className="min-h-screen flex items-center justify-center px-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-60" />
              </Transition.Child>
              
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0 scale-95"
              >
                <div className="inline-block transition-all text-center transform rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 dark:text-neutral-100 shadow-xl px-8 py-4">
                    <div className="mx-auto h-[200px] w-[200px]">
                    <Lottie
                      animationData={animationData}
                      loop
                      autoplay
                    />
                  </div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-neutral-200">
                    {t('creation')} {t('enCours')}
                  </h3>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
};

export default HeroSearchForm;
